import fetch from 'node-fetch';
import type { z } from 'zod/v4';
import { type HasNoRequiredProperties } from '@epihub/core/src/ts';
import { type PathParams } from '@epihub/core/src/server';
import { methodsWithoutBody } from '@epihub/core/src/server';
import { GenericError, normalizeError } from '@epihub/core/src/errors';
import { _isEmpty } from '@epihub/core/src/fn/object';
import { createPathname } from './createPathname';
import type { Procedure } from './procedure';

export type ProcedureFetchParams<Def extends Procedure> =
  & (
    HasNoRequiredProperties<z.infer<Def['query']>> extends true ? { query?: z.infer<Def['query']> }
      : { query: z.infer<Def['query']> }
  )
  & (
    HasNoRequiredProperties<PathParams<Def['path']>> extends true ? { params?: z.infer<Def['params']> }
      : { params: z.infer<Def['params']> }
  )
  & (
    HasNoRequiredProperties<z.infer<Def['body']>> extends true ? { body?: z.infer<Def['body']> }
      : { body: z.infer<Def['body']> }
  );

export type ProcedureFetchOptions<Def extends Procedure> =
  & ProcedureFetchParams<Def>
  & {
    baseURL: string;
    credentials?: 'include';
  }
  & (
    HasNoRequiredProperties<z.infer<Def['headers']>> extends true ? { headers?: z.infer<Def['headers']> }
      : { headers: z.infer<Def['headers']> }
  );

export type FetchedProcedureResult<
  Def extends Procedure,
  Status extends number = 200,
> = z.infer<Def['responses'][Status]>;

export async function fetchProcedure<Def extends Procedure>(
  def: Def,
  options: ProcedureFetchOptions<Def>,
): Promise<FetchedProcedureResult<Def>> {
  const pathname = createPathname(def, options.params ?? {});
  const url = new URL(`${options.baseURL}${pathname}`);

  for (const [name, value] of Object.entries(options.query ?? {})) {
    url.searchParams.set(name, `${value}`);
  }

  if (methodsWithoutBody.includes(def.method.toLowerCase() as any) && !_isEmpty(options.body)) {
    throw new GenericError({
      scope: 'fetchProcedure',
      message: `Request ${def.method}:${pathname} should not have body`,
    });
  }

  const response = await fetch(url.toString(), {
    method: def.method.toUpperCase(),
    body: (options.body && !methodsWithoutBody.includes(def.method as (typeof methodsWithoutBody)[number]))
      ? JSON.stringify(options.body)
      : undefined,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
    },

    // @ts-expect-error
    credentials: options.credentials,
  });

  const json = await response.json();
  if (!response.ok) {
    const err = normalizeError(json);
    throw err;
  }

  return json;
}
